/**
 * Client-side PDF Document Generator
 * Uses jsPDF, html2canvas, and jspdf-autotable for Azure serverless compatibility
 */

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import autoTable from 'jspdf-autotable';
import { formatCurrency as formatCurrencyBase } from './formatters';

// Page configuration constants
const PAGE_CONFIG = {
  format: 'a4',
  orientation: 'portrait',
  unit: 'mm',
  margins: {
    top: 20,
    right: 15,
    bottom: 20,
    left: 15,
  },
};

// Theme colors (matching Select Africa theme)
const COLORS = {
  primary: '#DD052B',
  secondary: '#00967F',
  info: '#8FD1E3',
  warning: '#BF883C',
  dark: '#1D292F',
  black: '#231F20',
  white: '#FFFFFF',
  grey: '#666666',
  lightGrey: '#f5f5f5',
};

/**
 * Generate PDF from HTML element using html2canvas
 * @param {string} elementId - The ID of the HTML element to convert
 * @param {string} filename - The filename for the downloaded PDF
 * @param {Object} options - Additional options
 * @returns {Promise<Blob>} - The generated PDF as a Blob
 */
export const generatePDFFromElement = async (elementId, filename = 'document.pdf', options = {}) => {
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error(`Element with ID "${elementId}" not found`);
  }

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false,
    ...options.canvasOptions,
  });

  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF({
    orientation: options.orientation || PAGE_CONFIG.orientation,
    unit: PAGE_CONFIG.unit,
    format: PAGE_CONFIG.format,
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgWidth = pageWidth - PAGE_CONFIG.margins.left - PAGE_CONFIG.margins.right;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  let heightLeft = imgHeight;
  let position = PAGE_CONFIG.margins.top;

  pdf.addImage(imgData, 'PNG', PAGE_CONFIG.margins.left, position, imgWidth, imgHeight);
  heightLeft -= (pageHeight - PAGE_CONFIG.margins.top - PAGE_CONFIG.margins.bottom);

  while (heightLeft > 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', PAGE_CONFIG.margins.left, position, imgWidth, imgHeight);
    heightLeft -= (pageHeight - PAGE_CONFIG.margins.top - PAGE_CONFIG.margins.bottom);
  }

  if (options.returnBlob) {
    return pdf.output('blob');
  }

  pdf.save(filename);
  return pdf;
};

/**
 * Generate a contract PDF programmatically using jsPDF
 * @param {Object} contractData - Contract data object
 * @param {string} filename - The filename for the downloaded PDF
 * @param {Object} options - Additional options (roundingPrecision: number of decimal places)
 * @returns {Promise<jsPDF>} - The generated PDF document
 */
export const generateContractPDF = async (contractData, filename = 'contract.pdf', options = {}) => {
  const decimals = options.roundingPrecision ?? 3;
  const pdf = new jsPDF({
    orientation: PAGE_CONFIG.orientation,
    unit: PAGE_CONFIG.unit,
    format: PAGE_CONFIG.format,
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margins = PAGE_CONFIG.margins;
  const contentWidth = pageWidth - margins.left - margins.right;

  let yPosition = margins.top;

  // Helper functions
  const addText = (text, x, y, fontSize = 10, style = 'normal', color = COLORS.black) => {
    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', style);
    pdf.setTextColor(color);
    pdf.text(text, x, y);
    return pdf.getTextDimensions(text).h + 2;
  };

  const addLine = (y, color = COLORS.lightGrey) => {
    pdf.setDrawColor(color);
    pdf.setLineWidth(0.5);
    pdf.line(margins.left, y, pageWidth - margins.right, y);
  };

  const addSection = (title, y) => {
    pdf.setFillColor(COLORS.lightGrey);
    pdf.rect(margins.left, y, contentWidth, 8, 'F');
    addText(title, margins.left + 3, y + 5.5, 11, 'bold', COLORS.dark);
    return y + 12;
  };

  const addField = (label, value, y, labelWidth = 50) => {
    addText(label + ':', margins.left, y, 9, 'bold', COLORS.grey);
    addText(String(value || 'N/A'), margins.left + labelWidth, y, 9, 'normal', COLORS.black);
    return y + 6;
  };

  const checkPageBreak = (requiredSpace) => {
    if (yPosition + requiredSpace > pageHeight - margins.bottom) {
      pdf.addPage();
      yPosition = margins.top;
      return true;
    }
    return false;
  };

  // Header with company branding
  pdf.setFillColor(COLORS.primary);
  pdf.rect(0, 0, pageWidth, 25, 'F');

  addText('CAPITAL MANAGEMENT SYSTEM', margins.left, 10, 16, 'bold', COLORS.white);
  addText('Investment Contract', margins.left, 18, 12, 'normal', COLORS.white);

  yPosition = 35;

  // Contract Reference Banner
  pdf.setFillColor(COLORS.secondary);
  pdf.rect(margins.left, yPosition, contentWidth, 12, 'F');
  addText(`Contract Reference: ${contractData.contractReference || 'N/A'}`, margins.left + 3, yPosition + 8, 12, 'bold', COLORS.white);
  yPosition += 18;

  // Contract Details Section
  yPosition = addSection('CONTRACT DETAILS', yPosition);
  yPosition = addField('Contract Date', formatDate(contractData.contractDate), yPosition);
  yPosition = addField('Status', contractData.status, yPosition);
  yPosition = addField('Product', contractData.productName, yPosition);
  if (contractData.productDescription) {
    yPosition = addField('Description', contractData.productDescription, yPosition);
  }
  yPosition += 5;

  // Investor Details Section
  checkPageBreak(50);
  yPosition = addSection('INVESTOR DETAILS', yPosition);
  yPosition = addField('Name', contractData.investorName, yPosition);
  yPosition = addField('Email', contractData.investorEmail, yPosition);
  yPosition = addField('Phone', contractData.investorPhone, yPosition);
  yPosition += 5;

  // Financial Details Section
  checkPageBreak(60);
  yPosition = addSection('FINANCIAL DETAILS', yPosition);
  yPosition = addField('Capital Amount', formatCurrency(contractData.capitalAmount, contractData.currencyCode, decimals), yPosition);
  yPosition = addField('Interest Rate', `${contractData.interestRate}% (${contractData.rateType || 'Fixed'})`, yPosition);
  yPosition = addField('Currency', `${contractData.currencyCode} - ${contractData.currencyName}`, yPosition);
  yPosition = addField('Deposit Date', formatDate(contractData.depositDate), yPosition);
  yPosition = addField('Maturity Date', formatDate(contractData.maturityDate), yPosition);
  yPosition = addField('Term', `${contractData.term} ${contractData.termUnit || 'Months'}`, yPosition);
  yPosition += 5;

  // Broker Details (if applicable)
  if (contractData.brokerName) {
    checkPageBreak(40);
    yPosition = addSection('BROKER DETAILS', yPosition);
    yPosition = addField('Broker Name', contractData.brokerName, yPosition);
    yPosition = addField('Broker Email', contractData.brokerEmail, yPosition);
    yPosition = addField('Broker Fee', `${contractData.brokerFee}%`, yPosition);
    yPosition += 5;
  }

  // Collateral Details (if applicable)
  if (contractData.collateralType) {
    checkPageBreak(50);
    yPosition = addSection('COLLATERAL DETAILS', yPosition);
    yPosition = addField('Type', contractData.collateralType, yPosition);
    yPosition = addField('Value', formatCurrency(contractData.collateralValue, contractData.currencyCode, decimals), yPosition);
    yPosition = addField('Description', contractData.collateralDescription, yPosition);
    yPosition = addField('Provider', contractData.collateralProviderName, yPosition);
    yPosition += 5;
  }

  // Notes Section (if applicable)
  if (contractData.notes) {
    checkPageBreak(30);
    yPosition = addSection('NOTES', yPosition);
    const splitNotes = pdf.splitTextToSize(contractData.notes, contentWidth - 6);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(COLORS.black);
    pdf.text(splitNotes, margins.left + 3, yPosition);
    yPosition += splitNotes.length * 5 + 5;
  }

  // Footer
  const footerY = pageHeight - 15;
  addLine(footerY - 5, COLORS.grey);
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(COLORS.grey);
  pdf.text(`Generated on: ${new Date().toLocaleString()}`, margins.left, footerY);
  pdf.text('Capital Management System', pageWidth - margins.right, footerY, { align: 'right' });

  // Page numbers
  const pageCount = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(COLORS.grey);
    pdf.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
  }

  if (options.returnBlob) {
    return pdf.output('blob');
  }

  pdf.save(filename);
  return pdf;
};

/**
 * Generate an amortization schedule PDF with table
 * @param {Object} scheduleData - Schedule data including payments array
 * @param {string} filename - The filename for the downloaded PDF
 * @param {Object} options - Additional options (roundingPrecision: number of decimal places)
 * @returns {Promise<jsPDF>} - The generated PDF document
 */
export const generateAmortizationSchedulePDF = async (scheduleData, filename = 'amortization_schedule.pdf', options = {}) => {
  const decimals = options.roundingPrecision ?? 3;
  const pdf = new jsPDF({
    orientation: PAGE_CONFIG.orientation,
    unit: PAGE_CONFIG.unit,
    format: PAGE_CONFIG.format,
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const margins = PAGE_CONFIG.margins;
  const contentWidth = pageWidth - margins.left - margins.right;

  let yPosition = margins.top;

  // Header
  pdf.setFillColor(COLORS.primary);
  pdf.rect(0, 0, pageWidth, 25, 'F');

  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(COLORS.white);
  pdf.text('AMORTIZATION SCHEDULE', margins.left, 10);

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Contract: ${scheduleData.contractReference || 'N/A'}`, margins.left, 18);

  yPosition = 35;

  // Contract Summary
  pdf.setFillColor(COLORS.lightGrey);
  pdf.rect(margins.left, yPosition, contentWidth, 30, 'F');

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(COLORS.dark);

  const col1X = margins.left + 5;
  const col2X = margins.left + contentWidth / 2;

  pdf.text('Investor:', col1X, yPosition + 8);
  pdf.text('Product:', col1X, yPosition + 14);
  pdf.text('Capital:', col1X, yPosition + 20);
  pdf.text('Rate:', col1X, yPosition + 26);

  pdf.text('Currency:', col2X, yPosition + 8);
  pdf.text('Deposit Date:', col2X, yPosition + 14);
  pdf.text('Maturity Date:', col2X, yPosition + 20);
  pdf.text('Term:', col2X, yPosition + 26);

  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(COLORS.black);

  pdf.text(scheduleData.investorName || 'N/A', col1X + 30, yPosition + 8);
  pdf.text(scheduleData.productName || 'N/A', col1X + 30, yPosition + 14);
  pdf.text(formatCurrency(scheduleData.capitalAmount, scheduleData.currencyCode, decimals), col1X + 30, yPosition + 20);
  pdf.text(`${scheduleData.interestRate}%`, col1X + 30, yPosition + 26);

  pdf.text(scheduleData.currencyCode || 'N/A', col2X + 35, yPosition + 8);
  pdf.text(formatDate(scheduleData.depositDate), col2X + 35, yPosition + 14);
  pdf.text(formatDate(scheduleData.maturityDate), col2X + 35, yPosition + 20);
  pdf.text(`${scheduleData.term} ${scheduleData.termUnit || 'Months'}`, col2X + 35, yPosition + 26);

  yPosition += 40;

  // Payment Schedule Table
  if (scheduleData.payments && scheduleData.payments.length > 0) {
    const tableData = scheduleData.payments.map((payment, index) => [
      index + 1,
      formatDate(payment.dueDate),
      formatCurrency(payment.principal, scheduleData.currencyCode, decimals),
      formatCurrency(payment.interest, scheduleData.currencyCode, decimals),
      formatCurrency(payment.totalPayment, scheduleData.currencyCode, decimals),
      formatCurrency(payment.balance, scheduleData.currencyCode, decimals),
    ]);

    autoTable(pdf, {
      startY: yPosition,
      head: [['#', 'Due Date', 'Principal', 'Interest', 'Total Payment', 'Balance']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: COLORS.dark,
        textColor: COLORS.white,
        fontSize: 8,
        fontStyle: 'bold',
        halign: 'center',
      },
      bodyStyles: {
        fontSize: 8,
        halign: 'right',
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 10 },
        1: { halign: 'center', cellWidth: 25 },
      },
      margin: { left: margins.left, right: margins.right },
      didDrawPage: (data) => {
        // Add page footer
        const pageHeight = pdf.internal.pageSize.getHeight();
        pdf.setFontSize(8);
        pdf.setTextColor(COLORS.grey);
        pdf.text(`Generated on: ${new Date().toLocaleString()}`, margins.left, pageHeight - 10);
      },
    });

    // Add totals row
    const finalY = pdf.lastAutoTable.finalY + 5;
    const totals = calculateScheduleTotals(scheduleData.payments);

    pdf.setFillColor(COLORS.secondary);
    pdf.rect(margins.left, finalY, contentWidth, 8, 'F');

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(COLORS.white);
    pdf.text('TOTALS:', margins.left + 5, finalY + 5.5);
    pdf.text(`Principal: ${formatCurrency(totals.totalPrincipal, scheduleData.currencyCode, decimals)}`, margins.left + 40, finalY + 5.5);
    pdf.text(`Interest: ${formatCurrency(totals.totalInterest, scheduleData.currencyCode, decimals)}`, margins.left + 90, finalY + 5.5);
    pdf.text(`Total: ${formatCurrency(totals.grandTotal, scheduleData.currencyCode, decimals)}`, margins.left + 135, finalY + 5.5);
  }

  // Page numbers
  const pageCount = pdf.internal.getNumberOfPages();
  const pageHeight = pdf.internal.pageSize.getHeight();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(COLORS.grey);
    pdf.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
  }

  if (options.returnBlob) {
    return pdf.output('blob');
  }

  pdf.save(filename);
  return pdf;
};

/**
 * Generate a simple report PDF with custom sections
 * @param {Object} reportData - Report configuration
 * @param {string} filename - The filename for the downloaded PDF
 * @param {Object} options - Additional options
 * @returns {Promise<jsPDF>} - The generated PDF document
 */
export const generateReportPDF = async (reportData, filename = 'report.pdf', options = {}) => {
  const pdf = new jsPDF({
    orientation: options.orientation || PAGE_CONFIG.orientation,
    unit: PAGE_CONFIG.unit,
    format: PAGE_CONFIG.format,
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margins = PAGE_CONFIG.margins;

  // Header
  pdf.setFillColor(COLORS.primary);
  pdf.rect(0, 0, pageWidth, 20, 'F');

  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(COLORS.white);
  pdf.text(reportData.title || 'Report', margins.left, 13);

  let yPosition = 30;

  // Report date and metadata
  if (reportData.subtitle) {
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(COLORS.grey);
    pdf.text(reportData.subtitle, margins.left, yPosition);
    yPosition += 10;
  }

  // Add tables if provided
  if (reportData.tables) {
    for (const table of reportData.tables) {
      if (yPosition > pageHeight - 60) {
        pdf.addPage();
        yPosition = margins.top;
      }

      if (table.title) {
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(COLORS.dark);
        pdf.text(table.title, margins.left, yPosition);
        yPosition += 8;
      }

      autoTable(pdf, {
        startY: yPosition,
        head: [table.headers],
        body: table.data,
        theme: 'grid',
        headStyles: {
          fillColor: COLORS.dark,
          textColor: COLORS.white,
          fontSize: 8,
          fontStyle: 'bold',
        },
        bodyStyles: {
          fontSize: 8,
        },
        margin: { left: margins.left, right: margins.right },
      });

      yPosition = pdf.lastAutoTable.finalY + 15;
    }
  }

  // Footer with page numbers
  const pageCount = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(COLORS.grey);
    pdf.text(`Generated on: ${new Date().toLocaleString()}`, margins.left, pageHeight - 10);
    pdf.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
  }

  if (options.returnBlob) {
    return pdf.output('blob');
  }

  pdf.save(filename);
  return pdf;
};

/**
 * Preview PDF in a new window
 * @param {jsPDF} pdf - The PDF document to preview
 */
export const previewPDF = (pdf) => {
  const blob = pdf.output('blob');
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');

  // Clean up the URL after a delay
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

/**
 * Get PDF as data URL for embedding
 * @param {jsPDF} pdf - The PDF document
 * @returns {string} - Data URL string
 */
export const getPDFDataURL = (pdf) => {
  return pdf.output('datauristring');
};

// Helper functions

/**
 * Format date for display
 * @param {string|Date} date - Date to format
 * @returns {string} - Formatted date string
 */
const formatDate = (date) => {
  if (!date) return 'N/A';
  try {
    const d = new Date(date);
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return String(date);
  }
};

/**
 * Format currency for display
 * @param {number|string} amount - Amount to format
 * @param {string} currency - Currency code
 * @param {number} decimals - Number of decimal places
 * @returns {string} - Formatted currency string
 */
const formatCurrency = (amount, currency = 'USD', decimals = 2) => {
  if (!amount && amount !== 0) return 'N/A';
  const result = formatCurrencyBase(amount, currency, decimals);
  return result === '-' ? 'N/A' : result;
};

/**
 * Calculate totals for amortization schedule
 * @param {Array} payments - Array of payment objects
 * @returns {Object} - Totals object
 */
const calculateScheduleTotals = (payments) => {
  return payments.reduce(
    (acc, payment) => ({
      totalPrincipal: acc.totalPrincipal + (parseFloat(payment.principal) || 0),
      totalInterest: acc.totalInterest + (parseFloat(payment.interest) || 0),
      grandTotal: acc.grandTotal + (parseFloat(payment.totalPayment) || 0),
    }),
    { totalPrincipal: 0, totalInterest: 0, grandTotal: 0 }
  );
};

export default {
  generatePDFFromElement,
  generateContractPDF,
  generateAmortizationSchedulePDF,
  generateReportPDF,
  previewPDF,
  getPDFDataURL,
  PAGE_CONFIG,
  COLORS,
};
